// D38 Currency utilities — pure derive functions, no side effects.
// convertToVnd: convert an amount in any supported currency to VND using saved rates.
// formatMoneyIn: format an amount in its native currency for display.
import { formatMoney } from "../lib/date";
import type { Account, CurrencyRate } from "../types";

/** All ISO codes this app supports (VND is the base currency). */
export const SUPPORTED_CURRENCIES: string[] = [
  "VND", "USD", "EUR", "JPY", "GBP", "SGD", "THB", "CNY", "KRW", "AUD",
];

export type ConversionResult = {
  /** Amount expressed in VND (0 when the rate is missing — see missingRate) */
  vnd: number;
  /** True when no usable rate was found; the amount is EXCLUDED (vnd = 0) */
  missingRate: boolean;
};

/**
 * Convert `amount` (denominated in `currency`) to VND using the provided rate list.
 * - VND input passes through unchanged (missingRate = false).
 * - Unknown/invalid rate for a non-VND currency → vnd = 0 with missingRate = true.
 *   We EXCLUDE the unconverted amount from VND totals rather than fold a bogus
 *   1:1 value in (which silently understated foreign balances ~25,000×). Callers
 *   surface `missingRate`/`hasMissingRate` as a "chưa quy đổi" warning to the user.
 */
export function convertToVnd(
  amount: number,
  currency: string,
  rates: CurrencyRate[],
): ConversionResult {
  const code = currency.trim().toUpperCase();
  if (code === "VND") {
    return { vnd: amount, missingRate: false };
  }
  const rate = rates.find((r) => r.id === code);
  if (!rate || rate.rateToVnd <= 0) {
    return { vnd: 0, missingRate: true };
  }
  return { vnd: amount * rate.rateToVnd, missingRate: false };
}

/**
 * D46: quy đổi amount của 1 ledger entry (theo ví của nó) sang VND để gộp P&L.
 * Entry lưu amount ở tiền tệ của ví (accountId). Ví thiếu/unknown → coi là VND
 * (ví mặc định luôn VND). Với state toàn VND đây là pass-through → 0 rủi ro.
 */
export function entryAmountToVnd(
  amount: number,
  accountId: string | undefined,
  accounts: Account[],
  rates: CurrencyRate[],
): number {
  const acct = accountId ? accounts.find((a) => a.id === accountId) : undefined;
  return convertToVnd(amount, acct?.currency ?? "VND", rates).vnd;
}

/**
 * Format an amount in its native currency for display.
 * - "VND" → delegates to the shared formatMoney (vi-VN locale, ₫ symbol).
 * - Others → Intl.NumberFormat with style "currency"; falls back to
 *   `{amount.toLocaleString("vi-VN")} {currency}` if the locale/currency
 *   combination is not supported by the runtime.
 */
export function formatMoneyIn(amount: number, currency: string): string {
  const code = currency.trim().toUpperCase();
  if (code === "VND") {
    return formatMoney(amount);
  }
  try {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: code,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString("vi-VN")} ${code}`;
  }
}
